import { useState } from "react";
import { createBrowserRouter, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Layout } from "@aiskills/ui-kit";
import { api } from "./api";

function DashboardStub() {
  const cards = [
    { title: "Active candidates", metric: "—" },
    { title: "Cohorts running", metric: "—" },
    { title: "Assessments today", metric: "—" },
    { title: "Avg. completion", metric: "—" },
  ];
  return (
    <>
      <header className="aiskills-page__header">
        <div>
          <h1 className="aiskills-page__title">Mission control</h1>
          <p className="aiskills-page__subtitle">
            Live view of your AI Skills assessments. Detailed analytics ship in SP-8.
          </p>
        </div>
      </header>
      <div className="aiskills-grid">
        {cards.map((c) => (
          <div key={c.title} className="aiskills-card">
            <div className="aiskills-card__title">{c.title}</div>
            <div className="aiskills-card__metric">{c.metric}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function PlaceholderPage({ title }: { title: string }) {
  return (
    <>
      <header className="aiskills-page__header">
        <div>
          <h1 className="aiskills-page__title">{title}</h1>
          <p className="aiskills-page__subtitle">This page is wired; content lands in a subsequent task.</p>
        </div>
      </header>
      <div className="aiskills-card">
        <p style={{ margin: 0 }}>Coming soon.</p>
      </div>
    </>
  );
}

const navItems: { to: string; label: string; section?: string; end?: boolean }[] = [
  { section: "Workspace" },
  { to: "/", label: "Dashboard", end: true },
  { to: "/candidates", label: "Candidates" },
  { to: "/cohorts", label: "Cohorts" },
  { to: "/assignments", label: "Assignments" },
  { section: "Tenant" },
  { to: "/institutions", label: "Institutions" },
  { to: "/settings/security", label: "Security" },
  { to: "/audit", label: "Audit log" },
];

function LogoutButton() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="aiskills-btn aiskills-btn--ghost aiskills-btn--sm"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await api.post("/v1/auth/logout");
        } catch {
          // session already expired or 4xx — proceed to login regardless
        }
        navigate("/login", { replace: true });
      }}
    >
      <span className="aiskills-btn__label">{busy ? "Signing out…" : "Log out"}</span>
    </button>
  );
}

function Chrome() {
  return (
    <Layout
      header={
        <>
          <span className="aiskills-brand">
            <span className="aiskills-brand__mark" aria-hidden="true">AI</span>
            <span className="aiskills-brand__name">AI Skills</span>
            <span className="aiskills-brand__tag">Admin</span>
          </span>
          <span className="aiskills-header__actions">
            <span className="aiskills-header__pill">Staging</span>
            <LogoutButton />
          </span>
        </>
      }
      nav={
        <ul className="aiskills-nav">
          {navItems.map((item, i) =>
            item.section ? (
              <li key={`s-${i}`} className="aiskills-nav__section">{item.section}</li>
            ) : (
              <li key={item.to}>
                <NavLink
                  to={item.to!}
                  end={item.end}
                  className={({ isActive }) =>
                    "aiskills-nav__link" + (isActive ? " is-active" : "")
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            )
          )}
        </ul>
      }
      footer={<span>AI Skills · {new Date().getFullYear()} · v0.1 (SP-1)</span>}
    >
      <Outlet />
    </Layout>
  );
}

export const router = createBrowserRouter([
  {
    path: "/login",
    lazy: async () => ({
      Component: (await import("./pages/Login")).Login,
    }),
  },
  {
    path: "/totp",
    lazy: async () => ({
      Component: (await import("./pages/TotpVerify")).TotpVerify,
    }),
  },
  {
    path: "/activate",
    lazy: async () => ({
      Component: (await import("./pages/Activate")).Activate,
    }),
  },
  {
    path: "/reset",
    lazy: async () => ({
      Component: (await import("./pages/ResetPassword")).ResetPassword,
    }),
  },
  {
    element: <Chrome />,
    children: [
      { path: "/", element: <DashboardStub /> },
      {
        path: "/settings/security",
        lazy: async () => ({
          Component: (await import("./pages/SecuritySettings")).SecuritySettings,
        }),
      },
      {
        path: "/candidates",
        lazy: async () => ({
          Component: (await import("./pages/Candidates")).Candidates,
        }),
      },
      {
        path: "/candidates/new",
        lazy: async () => ({
          Component: (await import("./pages/CandidateCreate")).CandidateCreate,
        }),
      },
      {
        path: "/candidates/bulk",
        lazy: async () => ({
          Component: (await import("./pages/CandidateBulk")).CandidateBulk,
        }),
      },
      {
        path: "/cohorts",
        lazy: async () => ({
          Component: (await import("./pages/Cohorts")).Cohorts,
        }),
      },
      {
        path: "/assignments",
        lazy: async () => ({
          Component: (await import("./pages/Assignments")).Assignments,
        }),
      },
      {
        path: "/institutions",
        lazy: async () => ({
          Component: (await import("./pages/Institutions")).Institutions,
        }),
      },
      {
        path: "/audit",
        lazy: async () => ({
          Component: (await import("./pages/AuditLog")).AuditLog,
        }),
      },
      { path: "*", element: <PlaceholderPage title="Not found" /> },
    ],
  },
]);
